
import React from 'react';

const TreatmentOptions = () => {
  const treatments = [
    {
      name: 'NAD+ IV Infusion',
      description: 'High-dose intravenous NAD+ therapy for maximum cellular uptake and immediate benefits',
      dosages: ['250mg', '500mg', '750mg', '1000mg'],
      duration: '2-4 hours',
      benefits: ['Immediate energy boost', 'Enhanced mental clarity', 'Cellular repair activation', 'Optimal bioavailability'],
      image: 'https://public.readdy.ai/ai/img_res/d95bacf322697b0bdf7ce0e6e6f5914e.jpg'
    },
    {
      name: 'NAD+ Injection Therapy',
      description: 'Subcutaneous or intramuscular NAD+ injections for convenient and effective treatment',
      dosages: ['50mg', '100mg', '200mg', '300mg'],
      duration: '5-10 minutes',
      benefits: ['Quick administration', 'Sustained release', 'Convenient scheduling', 'Cost-effective option'],
      image: 'https://readdy.ai/api/search-image?query=NAD%20%20injection%20therapy%20with%20medical%20syringe%20and%20vial%2C%20professional%20healthcare%20setting%2C%20clean%20white%20background%20with%20green%20accents%2C%20modern%20wellness%20clinic%20equipment&width=600&height=400&seq=nad-injection&orientation=landscape'
    },
    {
      name: 'NAD+ Nasal Spray',
      description: 'Convenient at-home NAD+ delivery through advanced nasal spray technology',
      dosages: ['10mg', '20mg', '30mg per spray'],
      duration: 'Instant',
      benefits: ['Daily maintenance', 'Easy self-administration', 'Rapid absorption', 'Travel-friendly'],
      image: 'https://readdy.ai/api/search-image?query=NAD%20%20nasal%20spray%20bottle%20with%20modern%20design%2C%20clean%20pharmaceutical%20packaging%2C%20white%20background%20with%20teal%20accents%2C%20professional%20wellness%20product%20photography&width=600&height=400&seq=nad-nasal&orientation=landscape'
    },
    {
      name: 'NAD+ Sublingual Tablets',
      description: 'High-absorption sublingual NAD+ tablets for consistent daily supplementation',
      dosages: ['25mg', '50mg', '100mg', '200mg'],
      duration: '2-5 minutes',
      benefits: ['Oral convenience', 'Consistent dosing', 'Long-term support', 'Portable format'],
      image: 'https://readdy.ai/api/search-image?query=NAD%20%20sublingual%20tablets%20in%20modern%20pharmaceutical%20packaging%2C%20clean%20white%20background%20with%20purple%20accents%2C%20professional%20supplement%20photography%20for%20wellness%20clinic&width=600&height=400&seq=nad-sublingual&orientation=landscape'
    },
    {
      name: 'NAD+ Combination Therapy',
      description: 'Synergistic protocols combining NAD+ with complementary nutrients and peptides',
      combinations: ['NAD+ + Glutathione', 'NAD+ + B-Complex', 'NAD+ + Peptides', 'NAD+ + Vitamin C'],
      duration: 'Varies',
      benefits: ['Enhanced efficacy', 'Comprehensive support', 'Personalized protocols', 'Synergistic effects'],
      image: 'https://readdy.ai/api/search-image?query=NAD%20%20combination%20therapy%20with%20multiple%20vials%20and%20IV%20setup%2C%20modern%20medical%20laboratory%20setting%2C%20clean%20white%20background%20with%20rainbow%20accents%2C%20advanced%20wellness%20treatment%20equipment&width=600&height=400&seq=nad-combo&orientation=landscape'
    },
    {
      name: 'NAD+ Maintenance Program',
      description: 'Ongoing NAD+ therapy protocols designed for long-term cellular health optimization',
      schedules: ['Weekly', 'Bi-weekly', 'Monthly', 'Quarterly'],
      duration: 'Ongoing',
      benefits: ['Sustained benefits', 'Age prevention', 'Optimal wellness', 'Convenient scheduling'],
      image: 'https://readdy.ai/api/search-image?query=NAD%20%20maintenance%20program%20calendar%20and%20treatment%20schedule%2C%20modern%20wellness%20clinic%20interior%2C%20clean%20white%20background%20with%20gold%20accents%2C%20professional%20healthcare%20planning%20setup&width=600&height=400&seq=nad-maintenance&orientation=landscape'
    }
  ];

  return (
    <section className="py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6">
            Comprehensive <strong>NAD+ Treatment</strong> Options
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Choose from our range of NAD+ delivery methods, each designed to optimize cellular function and enhance your overall health and vitality.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {treatments.map((treatment, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-video overflow-hidden">
                <img 
                  src={treatment.image} 
                  alt={treatment.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              <div className="p-6 lg:p-8">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                  {treatment.name}
                </h3>
                <p className="text-gray-600 mb-4 lg:mb-6 leading-relaxed">
                  {treatment.description}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4 lg:mb-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {treatment.dosages ? 'Dosages:' : treatment.combinations ? 'Combinations:' : treatment.schedules ? 'Schedules:' : 'Options:'}
                    </h4>
                    <div className="space-y-1">
                      {(treatment.dosages || treatment.combinations || treatment.schedules || []).map((item, idx) => (
                        <div key={idx} className="text-sm text-gray-600">{item}</div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Duration:</h4>
                    <div className="text-sm text-gray-600">{treatment.duration}</div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                  <ul className="space-y-2">
                    {treatment.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center">
                        <i className="ri-check-line text-green-600 text-lg mr-3"></i>
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 lg:mt-16">
          <div className="bg-green-50 rounded-2xl p-6 lg:p-8 max-w-4xl mx-auto">
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">
              Personalized NAD+ Protocols
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Our medical team creates customized NAD+ treatment plans based on your age, health status, goals, and lifestyle to ensure optimal results.
            </p>
            <button className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Get Your Custom Protocol
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TreatmentOptions;
